import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { apiGetAllUsers, apiCreateUser, apiUpdateUser, apiCreateProject, apiUpdateProject, apiChangePassword } from './api';
import { useGetUserProjectsQuery, useGetCustomersQuery, useGetCodeListQuery, EMPTY_ARRAY } from './store/futurizerApi';
import FuturizerLogo from './FuturizerLogo';
import PageLoader, { MiniSpinner } from './PageLoader';
import { formatDate } from './dateUtils';

const STATUS_CONFIG = {
  active:   { label: 'Active',      color: 'var(--success)',  bg: 'var(--success-bg)',  dot: 'var(--success)'  },
  review:   { label: 'In Review',   color: 'var(--warning)',  bg: 'var(--warning-bg)',  dot: 'var(--warning)'  },
  inactive: { label: 'Inactive',    color: 'var(--error)',    bg: 'var(--error-bg)',    dot: 'var(--error)'    },
};

const PAGE_SIZE = 5;

export default function ProjectSelectPage() {
  const { user, selectProject, logout } = useAuth();
  const navigate = useNavigate();

  const isAdmin = true;

  const [tab] = useState('projects');

  // ── Projects state ──
  // Backed by RTK Query (src/store/futurizerApi.js): `data` serves whatever's
  // already cached in Redux immediately on mount — no spinner — while a
  // background refetch (refetchOnMountOrArgChange) keeps it fresh. isLoading
  // is only true on the very first-ever fetch, when there's nothing cached yet.
  const {
    data: projectsRaw,
    isLoading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
  } = useGetUserProjectsQuery(user.id, { skip: !user?.id });
  // Referentially-stable fallback, not a `= []` destructuring default — see
  // EMPTY_ARRAY's comment for why that would risk an infinite render loop.
  const projects = projectsRaw ?? EMPTY_ARRAY;
  const allProjects = projects;

  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  const [selecting, setSelecting] = useState(null);

  // ── Users state ── (not yet migrated to RTK Query — separate concern)
  const [allUsers,     setAllUsers]     = useState([]);
  const [usersLoading,  setUsersLoading] = useState(true);
  const [usersError,    setUsersError]   = useState('');
  const [usersSearch,  setUsersSearch]  = useState('');
  const [modalUser,    setModalUser]    = useState(null); // null=closed | 'new' | user obj

  // ── Project modal state ──
  const [modalProject, setModalProject] = useState(null); // null=closed | 'new' | project obj
  const [changePwdOpen, setChangePwdOpen] = useState(false);

  const loading = projectsLoading || usersLoading;
  const error   = usersError || (projectsError ? 'Failed to load projects.' : '');

  useEffect(() => {
    setUsersLoading(true);
    apiGetAllUsers()
      .then(setAllUsers)
      .catch(() => setUsersError('Failed to load projects.'))
      .finally(() => setUsersLoading(false));
  }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function reloadUsers() {
    apiGetAllUsers().then(setAllUsers);
  }

  function reloadProjects() {
    refetchProjects();
  }

  const filtered = projects.filter(p => {
    const q = search.toLowerCase();
    return (
      p.name?.toLowerCase().includes(q) ||
      p.customer?.toLowerCase().includes(q)
    );
  });

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated   = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  function handleSearch(e) {
    setSearch(e.target.value);
    setPage(1);
  }

  function handleSelect(project) {
    if (selecting) return;
    setSelecting(project.id);
    selectProject(project);
    navigate('/app/workspace', { replace: true });
  }

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div style={styles.page}>

      {/* ── Top header ─────────────────────────────────────── */}
      <div style={styles.header}>
        <FuturizerLogo size={30} variant="white-full" />
        <div style={styles.headerRight}>
          <div style={styles.headerUser}>
            <div style={styles.headerAvatar}>{user?.name?.charAt(0)}</div>
            <div style={styles.headerUserInfo}>
              <span style={styles.headerUserName}>{user?.name}</span>
              <span style={styles.headerUserEmail}>{user?.email}</span>
            </div>
          </div>
          <button style={styles.logoutBtn} onClick={() => setChangePwdOpen(true)}>🔒 Change Password</button>
          <button style={styles.logoutBtn} onClick={handleLogout}>↩ Logout</button>
        </div>
      </div>

      {/* ── Body ───────────────────────────────────────────── */}
      <div style={styles.body}>

        {/* ── Tab switcher (admin only) ───────────────────────── */}
        {/* {isAdmin && (
          <div style={styles.tabs}>
            <button
              style={{ ...styles.tab, ...(tab === 'projects' ? styles.tabActive : {}) }}
              onClick={() => setTab('projects')}
            >
              📁 Projects
            </button>
            <button
              style={{ ...styles.tab, ...(tab === 'users' ? styles.tabActive : {}) }}
              onClick={() => setTab('users')}
            >
              👥 Users
            </button>
          </div>
        )} */}

        {/* ══ PROJECTS TAB ══════════════════════════════════════ */}
        {tab === 'projects' && (
          <>
            {loading && <PageLoader label="Loading your projects…" />}

            {!loading && (
              <div style={{ ...styles.headingBlock, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', maxWidth: 760, textAlign: 'left' }}>
                <div>
                  <h1 style={styles.title}>Select a Project</h1>
                  <p style={styles.subtitle}>
                    You have access to <strong>{projects.length}</strong> project{projects.length !== 1 ? 's' : ''}.
                    {/* {isAdmin && ' Click ✏️ to edit, or add a new one.'} */}
                  </p>
                </div>
                {/* {isAdmin && (
                  <button style={styles.addBtn} onClick={() => setModalProject('new')}>
                    + Add Project
                  </button>
                )} */}
              </div>
            )}

            {!loading && !error && (
              <div style={styles.searchWrap}>
                <span style={styles.searchIcon}>🔍</span>
                <input
                  style={styles.searchInput}
                  type="text"
                  autoComplete="off"
                  placeholder="Search by name or customer…"
                  value={search}
                  onChange={handleSearch}
                />
                {search && (
                  <button style={styles.searchClear} onClick={() => { setSearch(''); setPage(1); }}>✕</button>
                )}
              </div>
            )}

            {error && (
              <div className="error-alert" style={{ borderRadius: 'var(--radius-sm)', maxWidth: 700 }}>
                <span>{error}</span>
              </div>
            )}

            {!loading && !error && (
              <>
                {filtered.length === 0 ? (
                  <div style={styles.emptyState}>
                    <span style={{ fontSize: 32 }}>🔍</span>
                    <p style={{ margin: '8px 0 0', color: 'var(--text-secondary)', fontSize: 14 }}>
                      No projects match <strong>"{search}"</strong>
                    </p>
                  </div>
                ) : (
                  <div style={styles.grid} key={currentPage}>
                    {paginated.map((project, i) => (
                      <div
                        key={project.id}
                        style={{
                          animation:      'row-in 0.32s ease both',
                          animationDelay: `${i * 60}ms`,
                        }}
                      >
                        <ProjectCard
                          project={project}
                          selecting={selecting}
                          onSelect={handleSelect}
                          isAdmin={isAdmin}
                          onEdit={p => setModalProject(p)}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {totalPages > 1 && (
                  <div style={styles.pagination}>
                    <button
                      style={{ ...styles.pageBtn, ...(currentPage === 1 ? styles.pageBtnDisabled : {}) }}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      ‹ Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                      <button
                        key={n}
                        style={{ ...styles.pageBtn, ...(n === currentPage ? styles.pageBtnActive : {}) }}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      style={{ ...styles.pageBtn, ...(currentPage === totalPages ? styles.pageBtnDisabled : {}) }}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next ›
                    </button>
                    <span style={styles.pageInfo}>
                      {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ══ USERS TAB ═════════════════════════════════════════ */}
        {tab === 'users' && (
          <>
            <div style={{ ...styles.headingBlock, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', maxWidth: 760, textAlign: 'left' }}>
              <div>
                <h1 style={styles.title}>Users</h1>
                <p style={styles.subtitle}>
                  <strong>{allUsers.length}</strong> registered user{allUsers.length !== 1 ? 's' : ''}.
                  Double-click a user to edit.
                </p>
              </div>
              <button style={styles.addBtn} onClick={() => setModalUser('new')}>
                + Add User
              </button>
            </div>

            <div style={styles.searchWrap}>
              <span style={styles.searchIcon}>🔍</span>
              <input
                style={styles.searchInput}
                type="text"
                autoComplete="off"
                placeholder="Search by name, email or username…"
                value={usersSearch}
                onChange={e => setUsersSearch(e.target.value)}
              />
              {usersSearch && (
                <button style={styles.searchClear} onClick={() => setUsersSearch('')}>✕</button>
              )}
            </div>

            <div style={styles.grid}>
              {allUsers
                .filter(u => {
                  const q = usersSearch.toLowerCase();
                  return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q);
                })
                .map(u => (
                  <UserCard
                    key={u.id}
                    user={u}
                    onDoubleClick={() => setModalUser(u)}
                  />
                ))
              }
            </div>
          </>
        )}

        {/* ══ USER MODAL ════════════════════════════════════════ */}
        {modalUser && (
          <UserModal
            user={modalUser === 'new' ? null : modalUser}
            allProjects={allProjects}
            onClose={() => setModalUser(null)}
            onSave={() => { setModalUser(null); reloadUsers(); }}
          />
        )}

        {/* ══ PROJECT MODAL ═════════════════════════════════════ */}
        {changePwdOpen && (
          <ChangePasswordModal user={user} onClose={() => setChangePwdOpen(false)} onPasswordChanged={handleLogout} />
        )}

        {modalProject && (
          <ProjectModal
            project={modalProject === 'new' ? null : modalProject}
            onClose={() => setModalProject(null)}
            onSave={() => { setModalProject(null); reloadProjects(); }}
          />
        )}

      </div>
    </div>
  );
}

function ProjectCard({ project, selecting, onSelect, isAdmin, onEdit }) {
  const status     = STATUS_CONFIG[project.status] ?? STATUS_CONFIG.active;
  const isSelecting = selecting === project.id;
  const inactive   = project.status === 'inactive';
  const dimmed     = selecting && !isSelecting;

  return (
    <div
      role="button"
      tabIndex={inactive ? -1 : 0}
      title={inactive ? 'This project is inactive and cannot be selected.' : 'Double-click to edit'}
      style={{
        ...styles.card,
        opacity:     inactive ? 0.5 : dimmed ? 0.45 : 1,
        borderColor: isSelecting ? 'var(--primary)' : 'var(--border)',
        boxShadow:   isSelecting ? '0 0 0 3px rgba(230,81,0,0.15), var(--shadow-hover)' : 'var(--shadow-card)',
        cursor:      inactive ? 'not-allowed' : selecting ? 'default' : 'pointer',
        pointerEvents: inactive ? 'none' : 'auto',
      }}
      onClick={() => !inactive && onSelect(project)}
      onDoubleClick={e => { if (inactive) return; e.stopPropagation(); onEdit(project); }}
      onKeyDown={e => e.key === 'Enter' && !inactive && onSelect(project)}
    >
      {/* ── Card header ──────────────────────────────────── */}
      <div style={styles.cardHeader}>
        <div style={styles.cardIconWrap}>
          <span style={styles.cardIconEmoji}>📁</span>
        </div>

        <div style={styles.cardTitleBlock}>
          <div style={styles.cardTitleRow}>
            <span style={styles.cardName}>{project.name}</span>
            {/* Status badge */}
            <span style={{ ...styles.statusBadge, color: status.color, background: status.bg }}>
              <span style={{ ...styles.statusDot, background: status.dot }} />
              {status.label}
            </span>
          </div>
          <span style={styles.cardCustomer}>
            {project.customer}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* {isAdmin && (
            <button
              style={styles.editBtn}
              onClick={e => { e.stopPropagation(); onEdit(project); }}
              title="Edit project"
            >
              ✏️
            </button>
          )} */}
          <div style={styles.cardAction}>
            {isSelecting
              ? <MiniSpinner size={20} color="var(--primary)" />
              : inactive
                ? <span style={{ fontSize: 16, opacity: 0.4 }}>🔒</span>
                : <span style={styles.arrow}>→</span>
            }
          </div>
        </div>

      </div>

      {/* ── Description ──────────────────────────────────── */}
      <p style={styles.cardDesc}>{project.description}</p>

      {/* ── Divider ──────────────────────────────────────── */}
      <div style={styles.divider} />

      {/* ── Stats row ────────────────────────────────────── */}
      <div style={styles.statsRow}>
        <Stat icon="🏷️" label="Project ID"   value={`#${project.id}`} />
        <Stat icon="🏢" label="Customer"     value={project.customer || (project.customerId != null ? `#${project.customerId}` : '—')} />
        {/* <Stat icon="🖥️" label="Environment"  value={project.environment} /> */}
        <Stat icon="📋" label="Standards"    value={project.standardsCount} />
        <Stat icon="✅" label="Processed Tasks" value={project.resourceCount} />
        <Stat icon="📅" label="Created"       value={formatDate(project.createdAt)} />
      </div>

    </div>
  );
}

const ROLE_STYLE = {
  admin: { label: 'Admin', color: 'var(--primary)',    bg: 'rgba(230,81,0,0.08)' },
  user:  { label: 'User',  color: 'var(--text-muted)', bg: 'var(--bg-table-row-alt)' },
};

function UserCard({ user: u, onDoubleClick }) {
  const role = ROLE_STYLE[u.role] ?? ROLE_STYLE.user;
  return (
    <div
      style={{ ...styles.userCard, cursor: 'default' }}
      onDoubleClick={onDoubleClick}
      title="Double-click to edit"
    >
      <div style={styles.userCardLeft}>
        <div style={{
          ...styles.userCardAvatar,
          background: u.role === 'admin'
            ? 'linear-gradient(135deg,#E65100,#F4511E)'
            : 'linear-gradient(135deg,#1565C0,#1E88E5)',
        }}>
          {u.name.charAt(0).toUpperCase()}
        </div>
        <div style={styles.userCardInfo}>
          <span style={styles.userCardName}>{u.name}</span>
          <span style={styles.userCardEmail}>{u.email}</span>
        </div>
      </div>
      <div style={styles.userCardRight}>
        <span style={{ ...styles.userRoleBadge, color: role.color, background: role.bg }}>{role.label}</span>
        <span style={{ ...styles.userStatusBadge, color: u.enabled ? 'var(--success)' : 'var(--error)', background: u.enabled ? 'var(--success-bg)' : 'var(--error-bg)' }}>
          {u.enabled ? 'Active' : 'Disabled'}
        </span>
      </div>
      {u.projects?.length > 0 && (
        <div style={styles.userCardProjects}>
          <span style={styles.userCardProjectsLabel}>Projects</span>
          <div style={styles.userCardChips}>
            {u.projects.map(p => (
              <span key={p.id} style={styles.userCardChip}>{p.name}</span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectModal({ project, onClose, onSave }) {
  const isEdit = !!project;

  const {
    data: customersRaw,
    isLoading: customersLoading,
    error: customersError,
  } = useGetCustomersQuery();
  const {
    data: codeListRowsRaw,
    isLoading: codeListLoading,
    error: codeListError,
  } = useGetCodeListQuery();
  // Referentially-stable fallbacks — see EMPTY_ARRAY's comment.
  const customers = customersRaw ?? EMPTY_ARRAY;
  const codeListRows = codeListRowsRaw ?? EMPTY_ARRAY;

  // type → [{id, label}]
  const codeLists = useMemo(() => {
    const grouped = {};
    for (const c of codeListRows) {
      if (!grouped[c.codeListType]) grouped[c.codeListType] = [];
      grouped[c.codeListType].push({ id: c.codeListId, label: c.codeListDescription });
    }
    return grouped;
  }, [codeListRows]);

  const [f, setF] = useState({
    projectName:               project?.name        ?? '',
    projectCode:               project?.projectCode ?? '',
    fkCustomerId:              project?.customerId  ?? '',
    description:               project?.description ?? '',
    fkCodeListDomain:          '',
    fkCodeListSourcePlatform:  '',
    fkCodeListPbVersion:       '',
    pblCount:                  '',
    fkCodeListDesiredFrontend: '',
    frontendVersion:           '',
    fkCodeListDesiredBackend:  '',
    backendVersion:            '',
    fkCodeListDesiredDatabase: '',
    targetRepoUrl:             '',
    fkCodeListStatus:          '',
    fkCodeListPriority:        '',
    startDate:                 project?.startDate ?? '',
    targetEndDate:             '',
    actualEndDate:             '',
    projectAPIKey:             '',
  });

  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  function set(key) { return e => setF(prev => ({ ...prev, [key]: e.target.value })); }

  useEffect(() => {
    if (customersError || codeListError) setErr('Could not load form data.');
  }, [customersError, codeListError]);

  // One-time default-selection once both lists have loaded — guarded with a
  // ref so a later cache refresh (e.g. another tab creating a customer) can't
  // clobber the user's in-progress selection mid-edit.
  const defaultedRef = useRef(false);
  useEffect(() => {
    if (defaultedRef.current || customersLoading || codeListLoading) return;
    defaultedRef.current = true;

    setF(prev => {
      const next = { ...prev };
      if (!next.fkCustomerId) {
        // Editing an existing project: its customer only comes to us as a display
        // name (project.customer), not a numeric id — resolve it against the
        // customer list now that it's loaded. Fall back to the first customer
        // only for a brand-new project, or if the name can't be matched.
        const matched = isEdit && project?.customer
          ? customers.find(c => (c.companyName ?? c.legalName) === project.customer)
          : null;
        const fallback = matched ?? customers[0];
        if (fallback) next.fkCustomerId = String(fallback.customerId);
      }

      // default first option for each FK
      const map = {
        fkCodeListDomain:          'Domain',
        fkCodeListSourcePlatform:  'SourcePlatform',
        fkCodeListPbVersion:       'PbVersion',
        fkCodeListDesiredFrontend: 'Frontend',
        fkCodeListDesiredBackend:  'Backend',
        fkCodeListDesiredDatabase: 'Database',
        fkCodeListStatus:          'ProjectStatus',
        fkCodeListPriority:        'Priority',
      };
      for (const [field, type] of Object.entries(map)) {
        if (codeLists[type]?.length) next[field] = String(codeLists[type][0].id);
      }
      return next;
    });
  }, [customersLoading, codeListLoading, customers, codeLists, isEdit, project]);

  async function handleSave() {
    if (!f.projectName.trim()) { setErr('Project name is required.'); return; }
    if (!f.fkCustomerId)       { setErr('Please select a customer.');  return; }
    setSaving(true); setErr('');
    try {
      if (isEdit) await apiUpdateProject(project.id, { name: f.projectName, customerId: f.fkCustomerId, description: f.description, startDate: f.startDate });
      else        await apiCreateProject(f);
      onSave();
    } catch (e) {
      setErr(e.message); setSaving(false);
    }
  }

  function CLSelect({ label, field, type, required }) {
    const opts = codeLists[type] ?? [];
    return (
      <div style={mStyles.field}>
        <label style={mStyles.label}>{label}{required ? ' *' : ''}</label>
        <select style={mStyles.select} value={f[field]} onChange={set(field)}>
          {opts.length === 0 && <option value="">Loading…</option>}
          {opts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </div>
    );
  }

  return (
    <div style={mStyles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...mStyles.modal, maxWidth: 680 }}>
        <div style={mStyles.header}>
          <h2 style={mStyles.title}>{isEdit ? 'Edit Project' : 'Add Project'}</h2>
          <button style={mStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={mStyles.body}>
          {err && <div className="error-alert" style={{ marginBottom: 12 }}>{err}</div>}

          {/* ── Basic ─────────────────────────────────────── */}
          <div style={mStyles.sectionLabel}>Basic Info</div>
          <div style={mStyles.row}>
            <ModalField label="Project Name *" value={f.projectName} onChange={v => setF(p=>({...p, projectName: v}))} placeholder="e.g. Acme CRM" />
            <div style={mStyles.field}>
              <label style={mStyles.label}>Customer *</label>
              <select style={mStyles.select} value={f.fkCustomerId} onChange={set('fkCustomerId')}>
                {customers.length === 0 && <option value="">Loading…</option>}
                {customers.map(c => (
                  <option key={c.customerId} value={c.customerId}>
                    {c.companyName || c.legalName} (#{c.customerId})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={mStyles.row}>
            <ModalField label="Project Code" value={f.projectCode} onChange={v => setF(p=>({...p, projectCode: v}))} placeholder="e.g. ACC001" />
            <CLSelect label="Domain" field="fkCodeListDomain" type="Domain" />
          </div>

          {/* ── Source ────────────────────────────────────── */}
          <div style={mStyles.sectionLabel}>Source</div>
          <div style={mStyles.row}>
            <CLSelect label="Source Platform" field="fkCodeListSourcePlatform" type="SourcePlatform" />
            <CLSelect label="PB Version"      field="fkCodeListPbVersion"      type="PbVersion" />
          </div>
          <div style={mStyles.row}>
            <ModalField label="PBL Count" value={f.pblCount} onChange={v => setF(p=>({...p, pblCount: v}))} placeholder="0" type="number" />
            <div style={mStyles.field} />
          </div>

          {/* ── Target ────────────────────────────────────── */}
          <div style={mStyles.sectionLabel}>Target Stack</div>
          <div style={mStyles.row}>
            <CLSelect label="Frontend" field="fkCodeListDesiredFrontend" type="Frontend" />
            <ModalField label="Frontend Version" value={f.frontendVersion} onChange={v => setF(p=>({...p, frontendVersion: v}))} placeholder="e.g. 18" />
          </div>
          <div style={mStyles.row}>
            <CLSelect label="Backend" field="fkCodeListDesiredBackend" type="Backend" />
            <ModalField label="Backend Version" value={f.backendVersion} onChange={v => setF(p=>({...p, backendVersion: v}))} placeholder="e.g. 8.0" />
          </div>
          <div style={mStyles.row}>
            <CLSelect label="Database" field="fkCodeListDesiredDatabase" type="Database" />
            <ModalField label="Target Repo URL" value={f.targetRepoUrl} onChange={v => setF(p=>({...p, targetRepoUrl: v}))} placeholder="https://github.com/…" />
          </div>

          {/* ── Status & Priority ─────────────────────────── */}
          <div style={mStyles.sectionLabel}>Status & Schedule</div>
          <div style={mStyles.row}>
            <CLSelect label="Status"   field="fkCodeListStatus"   type="ProjectStatus" />
            <CLSelect label="Priority" field="fkCodeListPriority" type="Priority" />
          </div>
          <div style={mStyles.row}>
            <ModalField label="Start Date"      value={f.startDate}     onChange={v => setF(p=>({...p, startDate: v}))}     type="date" />
            <ModalField label="Target End Date" value={f.targetEndDate} onChange={v => setF(p=>({...p, targetEndDate: v}))} type="date" />
          </div>
          <div style={mStyles.row}>
            <ModalField label="Actual End Date" value={f.actualEndDate} onChange={v => setF(p=>({...p, actualEndDate: v}))} type="date" />
            <ModalField label="Project API Key" value={f.projectAPIKey} onChange={v => setF(p=>({...p, projectAPIKey: v}))} placeholder="Optional" />
          </div>

          {/* ── Description ───────────────────────────────── */}
          <div style={mStyles.field}>
            <label style={mStyles.label}>Description</label>
            <textarea
              style={{ ...mStyles.input, resize: 'vertical', minHeight: 72 }}
              value={f.description}
              onChange={e => setF(p=>({...p, description: e.target.value}))}
              placeholder="Brief description of the project…"
            />
          </div>
        </div>

        <div style={mStyles.footer}>
          <button style={mStyles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={mStyles.saveBtn}   onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Project'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserModal({ user, allProjects, onClose, onSave }) {
  const isEdit = !!user;
  const uniqueProjects = allProjects.filter((p, i, arr) => arr.findIndex(x => x.name === p.name) === i);

  const [name,     setName]     = useState(user?.name     ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [email,    setEmail]    = useState(user?.email    ?? '');
  const [password, setPassword] = useState('');
  const [role,     setRole]     = useState(user?.role     ?? 'user');
  const [enabled,  setEnabled]  = useState(user?.enabled  ?? true);
  const [selected, setSelected] = useState(new Set(user?.projects?.map(p => p.id) ?? []));
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState('');

  function toggleProject(id) {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  }

  async function handleSave() {
    if (!name.trim() || !username.trim() || !email.trim()) {
      setErr('Name, username and email are required.'); return;
    }
    if (!isEdit && !password) {
      setErr('Password is required for new users.'); return;
    }
    setSaving(true); setErr('');
    try {
      const data = { name, username, email, role, enabled, projectIds: [...selected], ...(password ? { password } : {}) };
      if (isEdit) await apiUpdateUser(user.id, data);
      else        await apiCreateUser(data);
      onSave();
    } catch (e) {
      setErr(e.message); setSaving(false);
    }
  }

  return (
    <div style={mStyles.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={mStyles.modal}>
        <div style={mStyles.header}>
          <h2 style={mStyles.title}>{isEdit ? 'Edit User' : 'Add User'}</h2>
          <button style={mStyles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div style={mStyles.body}>
          {err && <div className="error-alert" style={{ marginBottom: 12 }}>{err}</div>}

          <div style={mStyles.row}>
            <ModalField label="Full Name *"  value={name}     onChange={setName}     placeholder="Full name" />
            <ModalField label="Username *"   value={username} onChange={setUsername} placeholder="username" />
          </div>
          <div style={mStyles.row}>
            <ModalField label="Email *"      value={email}    onChange={setEmail}    placeholder="email@example.com" />
            <ModalField
              label={isEdit ? 'New Password' : 'Password *'}
              value={password} onChange={setPassword}
              placeholder={isEdit ? 'Leave blank to keep current' : 'Password'}
              type="password"
            />
          </div>
          <div style={mStyles.row}>
            <div style={mStyles.field}>
              <label style={mStyles.label}>Role</label>
              <select style={mStyles.select} value={role} onChange={e => setRole(e.target.value)}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={mStyles.field}>
              <label style={mStyles.label}>Status</label>
              <select style={mStyles.select} value={enabled ? 'active' : 'disabled'} onChange={e => setEnabled(e.target.value === 'active')}>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>

          <div style={mStyles.section}>
            <label style={mStyles.sectionLabel}>Assign Projects</label>
            <div style={mStyles.projectList}>
              {uniqueProjects.map(p => (
                <label key={p.id} style={{ ...mStyles.projectItem, background: selected.has(p.id) ? 'var(--info-bg)' : 'var(--bg-table-row-alt)', borderColor: selected.has(p.id) ? 'rgba(21,101,192,0.3)' : 'var(--border)' }}>
                  <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleProject(p.id)} style={mStyles.checkbox} />
                  <div style={mStyles.projectItemInfo}>
                    <span style={mStyles.projectItemName}>{p.name}</span>
                    <span style={mStyles.projectItemCustomer}>{p.customer}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div style={mStyles.footer}>
          <button style={mStyles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={mStyles.saveBtn}   onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Mirrors the API's ValidateNewPassword (UserService) so errors show before submit.
const PWD_MIN_LENGTH = 8;
const PWD_MAX_LENGTH = 64;

export function ChangePasswordModal({ user, onClose, onPasswordChanged }) {
  const [current, setCurrent] = useState('');
  const [next,    setNext]    = useState('');
  const [confirm, setConfirm] = useState('');
  const [show,    setShow]    = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const [done,    setDone]    = useState(false);

  const rules = [
    { ok: next.length >= PWD_MIN_LENGTH && next.length <= PWD_MAX_LENGTH, label: `${PWD_MIN_LENGTH}–${PWD_MAX_LENGTH} characters` },
    { ok: /[A-Z]/.test(next) && /[a-z]/.test(next), label: 'Upper and lower case letters' },
    { ok: /\d/.test(next), label: 'At least one number' },
    { ok: next.length > 0 && next === next.trim() && [...next].every(c => c.charCodeAt(0) <= 127), label: 'No leading/trailing spaces or special unicode' },
  ];
  const mismatch = confirm.length > 0 && next !== confirm;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSave = current && rules.every(r => r.ok) && next === confirm && !sameAsCurrent && !saving;

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setErr('');
    try {
      await apiChangePassword({ userId: user?.id, currentPassword: current, newPassword: next, confirmPassword: confirm });
      setDone(true);
    } catch (e) {
      setErr(e.message || 'Could not change password.');
    } finally {
      setSaving(false);
    }
  }

  function pwdInput(value, setValue, placeholder, autoComplete) {
    return (
      <div style={mStyles.pwdWrap}>
        <input
          style={{ ...mStyles.input, width: '100%', boxSizing: 'border-box', paddingRight: 38 }}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSave()}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          style={mStyles.pwdEye}
          onClick={() => setShow(s => !s)}
          aria-label={show ? 'Hide passwords' : 'Show passwords'}
          tabIndex={-1}
        >{show ? '🙈' : '👁'}</button>
      </div>
    );
  }

  return (
    <div style={mStyles.overlay} onClick={e => e.target === e.currentTarget && !saving && !done && onClose()}>
      <div style={{ ...mStyles.modal, maxWidth: 440 }}>
        <div style={mStyles.header}>
          <h2 style={mStyles.title}>🔒 Change Password</h2>
          <button style={mStyles.closeBtn} onClick={done ? onPasswordChanged : onClose} disabled={saving}>✕</button>
        </div>

        {done ? (
          <>
            <div style={{ ...mStyles.body, alignItems: 'center', textAlign: 'center', padding: '32px 22px' }}>
              <div style={{ fontSize: 36 }}>✅</div>
              <div style={mStyles.title}>Password updated</div>
              <div style={mStyles.pwdHint}>For security you've been signed out. Sign in again with your new password.</div>
            </div>
            <div style={mStyles.footer}>
              <button style={mStyles.saveBtn} onClick={onPasswordChanged}>Sign in again</button>
            </div>
          </>
        ) : (
          <>
            <div style={mStyles.body}>
              <div style={mStyles.pwdHint}>
                Signed in as <strong>{user?.name}</strong>
              </div>
              {/* Hidden username next to the password fields: without it the browser's
                  password manager treats the nearest text box on the page (e.g. a
                  tab's search field) as the username and autofills it there. */}
              <input
                type="text"
                name="username"
                autoComplete="username"
                value={user?.username ?? user?.email ?? user?.name ?? ''}
                readOnly
                hidden
              />
              {err && <div className="error-alert">{err}</div>}

              <div style={mStyles.field}>
                <label style={mStyles.label}>Current Password *</label>
                {pwdInput(current, setCurrent, 'Enter current password', 'current-password')}
              </div>

              <div style={mStyles.field}>
                <label style={mStyles.label}>New Password *</label>
                {pwdInput(next, setNext, 'Enter new password', 'new-password')}
                <ul style={mStyles.pwdRules}>
                  {rules.map(r => (
                    <li key={r.label} style={{ ...mStyles.pwdRule, color: r.ok ? '#2e7d32' : 'var(--text-muted)' }}>
                      {r.ok ? '✓' : '○'} {r.label}
                    </li>
                  ))}
                </ul>
                {sameAsCurrent && <span style={mStyles.pwdError}>New password must differ from the current one.</span>}
              </div>

              <div style={mStyles.field}>
                <label style={mStyles.label}>Confirm New Password *</label>
                {pwdInput(confirm, setConfirm, 'Re-enter new password', 'new-password')}
                {mismatch && <span style={mStyles.pwdError}>Passwords do not match.</span>}
              </div>
            </div>

            <div style={mStyles.footer}>
              <button style={mStyles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
              <button
                style={{ ...mStyles.saveBtn, opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}
                onClick={handleSave}
                disabled={!canSave}
              >
                {saving ? 'Updating…' : 'Update Password'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ModalField({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div style={mStyles.field}>
      <label style={mStyles.label}>{label}</label>
      <input
        style={mStyles.input}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div style={styles.stat}>
      <span style={styles.statIcon}>{icon}</span>
      <div style={styles.statBody}>
        <span style={styles.statLabel}>{label}</span>
        <span style={styles.statValue}>{value}</span>
      </div>
    </div>
  );
}

/* ── Styles ───────────────────────────────────────────────────────────────── */
const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--bg-page)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'var(--font-family)',
  },

  /* Header */
  header: {
    background: 'linear-gradient(90deg,#7F2000,#E65100)',
    padding: 'clamp(10px, 3vw, 12px) clamp(16px, 4vw, 36px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 10,
    boxShadow: '0 2px 16px rgba(13,27,64,0.2)',
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: 20 },
  headerUser: { display: 'flex', alignItems: 'center', gap: 10 },
  headerAvatar: {
    width: 34, height: 34, borderRadius: '50%',
    background: 'rgba(255,255,255,0.18)', border: '2px solid rgba(255,255,255,0.3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0,
  },
  headerUserInfo: { display: 'flex', flexDirection: 'column' },
  headerUserName: { fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.2 },
  headerUserEmail: { fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.3 },
  logoutBtn: {
    background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
    color: 'rgba(255,255,255,0.85)', borderRadius: 7, padding: '7px 16px',
    fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s',
  },

  /* Body */
  body: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', padding: 'clamp(24px, 5vw, 52px) clamp(16px, 4vw, 24px) clamp(32px, 5vw, 60px)',
  },
  headingBlock: { textAlign: 'center', marginBottom: 36 },
  title: {
    fontSize: 28, fontWeight: 800, color: 'var(--text-primary)',
    margin: '0 0 8px', letterSpacing: '-0.5px',
  },
  subtitle: {
    fontSize: 'var(--font-size-base)', color: 'var(--text-secondary)', margin: 0,
  },

  /* Search */
  searchWrap: {
    position: 'relative', display: 'flex', alignItems: 'center',
    width: '100%', maxWidth: 760, marginBottom: 20,
  },
  searchIcon: {
    position: 'absolute', left: 14, fontSize: 15, pointerEvents: 'none',
  },
  searchInput: {
    width: '100%', padding: '10px 40px 10px 40px',
    borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--border)',
    background: 'var(--bg-card)', color: 'var(--text-primary)',
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
    fontFamily: 'var(--font-family)',
  },
  searchClear: {
    position: 'absolute', right: 12, background: 'none', border: 'none',
    cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, padding: 4,
  },

  /* Empty state */
  emptyState: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '48px 0', width: '100%', maxWidth: 760,
  },

  /* Pagination */
  pagination: {
    display: 'flex', alignItems: 'center', gap: 6,
    marginTop: 24, flexWrap: 'wrap', justifyContent: 'center',
  },
  pageBtn: {
    padding: '6px 14px', borderRadius: 7,
    border: '1.5px solid var(--border)', background: 'var(--bg-card)',
    color: 'var(--text-primary)', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s',
  },
  pageBtnActive: {
    background: 'var(--primary)', color: '#fff', borderColor: 'var(--primary)',
  },
  pageBtnDisabled: {
    opacity: 0.4, cursor: 'default',
  },
  pageInfo: {
    fontSize: 12, color: 'var(--text-muted)', marginLeft: 8, fontWeight: 500,
  },

  /* Cards grid */
  grid: {
    display: 'flex', flexDirection: 'column', gap: 18,
    width: '100%', maxWidth: 760,
  },

  /* Project card */
  card: {
    background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)',
    border: '1.5px solid var(--border)', padding: '22px 24px 18px',
    transition: 'border-color 0.18s, box-shadow 0.18s, opacity 0.18s',
    display: 'flex', flexDirection: 'column', gap: 0,
  },
  cardHeader: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 12 },
  cardIconWrap: {
    width: 48, height: 48, borderRadius: 'var(--radius-sm)',
    background: 'var(--info-bg)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  cardIconEmoji: { fontSize: 22 },
  cardTitleBlock: { flex: 1, display: 'flex', flexDirection: 'column', gap: 4 },
  cardTitleRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  cardName: { fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.3px' },
  cardCustomer: { fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 },
  cardAction: { flexShrink: 0, display: 'flex', alignItems: 'center' },
  spinner: { width: 20, height: 20, borderWidth: 2, flexShrink: 0 },
  arrow: { fontSize: 22, color: 'var(--primary)', fontWeight: 700, lineHeight: 1 },

  statusBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '3px 10px', borderRadius: 'var(--radius-pill)',
    fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
  },
  statusDot: { width: 6, height: 6, borderRadius: '50%', flexShrink: 0 },

  cardDesc: {
    fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.65,
    margin: '0 0 16px', paddingLeft: 'clamp(0px, 5vw, 62px)',
  },

  divider: { height: 1, background: 'var(--border)', margin: '0 0 16px' },

  /* Stats */
  statsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 8, marginBottom: 16,
  },
  stat: {
    display: 'flex', alignItems: 'flex-start', gap: 6,
    background: 'var(--bg-table-row-alt)', borderRadius: 8,
    padding: '8px 10px',
  },
  statIcon: { fontSize: 14, lineHeight: 1.4, flexShrink: 0 },
  statBody: { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 },
  statLabel: { fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' },
  statValue: { fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'normal', wordBreak: 'break-word' },

  /* Footer */
  cardFooter: {
    display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
    gap: 12,
  },
  lastActivity: { fontSize: 11, color: 'var(--text-muted)', fontWeight: 500 },

  /* ── Tab switcher ─────────────────────────────────── */
  tabs: {
    display: 'flex', gap: 6,
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    borderRadius: 10, padding: 4,
    marginBottom: 28, alignSelf: 'center',
  },
  tab: {
    padding: '8px 22px', borderRadius: 8, border: 'none',
    background: 'transparent', color: 'var(--text-secondary)',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    transition: 'background 0.15s, color 0.15s',
  },
  tabActive: {
    background: 'var(--primary)', color: '#fff',
  },

  /* ── User card ────────────────────────────────────── */
  userCard: {
    background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)',
    border: '1.5px solid var(--border)', padding: '18px 20px',
    display: 'flex', flexDirection: 'column', gap: 14,
    boxShadow: 'var(--shadow-card)',
  },
  userCardLeft: { display: 'flex', alignItems: 'center', gap: 12 },
  userCardAvatar: {
    width: 42, height: 42, borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 16, fontWeight: 700, color: '#fff', flexShrink: 0,
  },
  userCardInfo:  { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  userCardName:  { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' },
  userCardEmail: { fontSize: 12, color: 'var(--text-muted)' },
  userCardRight: { display: 'flex', gap: 8, alignItems: 'center' },
  userRoleBadge: {
    display: 'inline-block', padding: '3px 10px',
    borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700,
  },
  userStatusBadge: {
    display: 'inline-block', padding: '3px 10px',
    borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700,
  },
  userCardProjects:      { display: 'flex', flexDirection: 'column', gap: 7 },
  userCardProjectsLabel: {
    fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.6px', color: 'var(--text-muted)',
  },
  userCardChips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  userCardChip: {
    display: 'inline-block', padding: '3px 10px',
    borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 600,
    background: 'var(--info-bg)', color: 'var(--primary)',
    border: '1px solid rgba(21,101,192,0.15)',
  },

  addBtn: {
    background: 'var(--primary)', color: '#fff',
    border: 'none', borderRadius: 8,
    padding: '9px 18px', fontSize: 13, fontWeight: 700,
    cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
  },
  editBtn: {
    background: 'rgba(255,255,255,0.85)', border: '1px solid var(--border)',
    borderRadius: 7, padding: '4px 8px', fontSize: 13,
    cursor: 'pointer', lineHeight: 1, flexShrink: 0,
  },
};

/* ── Modal styles ─────────────────────────────────────────────────────────── */
const mStyles = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'var(--bg-card)', borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border)', boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
    width: '100%', maxWidth: 560, maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    fontFamily: 'var(--font-family)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '18px 22px 14px', borderBottom: '1px solid var(--border)',
  },
  title: { fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', margin: 0 },
  closeBtn: {
    background: 'none', border: 'none', fontSize: 16,
    cursor: 'pointer', color: 'var(--text-muted)', padding: 4, lineHeight: 1,
  },
  body: { padding: '18px 22px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' },
  input: {
    padding: '8px 11px', borderRadius: 7,
    border: '1.5px solid var(--border)', background: 'var(--bg-page)',
    color: 'var(--text-primary)', fontSize: 13, outline: 'none',
    fontFamily: 'var(--font-family)',
  },
  select: {
    padding: '8px 11px', borderRadius: 7,
    border: '1.5px solid var(--border)', background: 'var(--bg-page)',
    color: 'var(--text-primary)', fontSize: 13, outline: 'none',
    fontFamily: 'var(--font-family)',
  },
  section: { display: 'flex', flexDirection: 'column', gap: 8 },
  sectionLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)' },
  projectList: { display: 'flex', flexDirection: 'column', gap: 6 },
  projectItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '9px 12px', borderRadius: 8,
    border: '1px solid var(--border)', cursor: 'pointer',
    transition: 'background 0.12s, border-color 0.12s',
  },
  checkbox: { width: 15, height: 15, accentColor: 'var(--primary)', cursor: 'pointer', flexShrink: 0 },
  projectItemInfo: { display: 'flex', flexDirection: 'column', gap: 1 },
  projectItemName: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' },
  projectItemCustomer: { fontSize: 11, color: 'var(--text-muted)' },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 10,
    padding: '14px 22px', borderTop: '1px solid var(--border)',
  },
  cancelBtn: {
    padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
    border: '1.5px solid var(--border)', background: 'var(--bg-page)',
    color: 'var(--text-secondary)', cursor: 'pointer',
  },
  saveBtn: {
    padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700,
    border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer',
  },
  pwdWrap:  { position: 'relative' },
  pwdEye: {
    position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 4,
  },
  pwdHint:  { fontSize: 12, color: 'var(--text-muted)' },
  pwdRules: { listStyle: 'none', margin: '4px 0 0', padding: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 12px' },
  pwdRule:  { fontSize: 11 },
  pwdError: { fontSize: 11, color: '#f85149' },
};
